import { useTranslation } from "react-i18next";
import { useFormContext, useWatch } from "react-hook-form";
import DatePicker from "~/molecules/DatePicker";
import { RichTextEditor } from "~/molecules/RichTextEditor";
import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import {
  buildWorkflowDateChain,
  getDateChainBounds,
  isBeforeToday,
} from "~/helpers/workflowDates";
import { type WorkflowEditorFormValues } from "../workflowEditorForm";
import { type WorkflowEditorMode } from "~/pages/workflow-editor/useWorkflowEditorPage";

interface Props {
  mode: WorkflowEditorMode;
}

export function WorkflowMetadataSection({ mode }: Props) {
  const { t } = useTranslation();
  const form = useFormContext<WorkflowEditorFormValues>();

  const isPreviewMode = mode.type === "previewing";
  const artifact = mode.release.artifact;
  const workflowHasEnded = artifact.publishedAt !== null;

  const reviewFrom = useWatch({ control: form.control, name: "reviewFrom" });
  const reviewTo = useWatch({ control: form.control, name: "reviewTo" });

  const dateBounds = getDateChainBounds(
    buildWorkflowDateChain(mode.steps, reviewFrom, reviewTo),
  );
  const reviewFromBounds = dateBounds.get("reviewFrom");
  const reviewToBounds = dateBounds.get("reviewTo");

  const createdFromTemplateLabel =
    artifact.workflowTemplateId != null
      ? t(($) => $.commons.workflow.createdFromTemplate, {
          name:
            artifact.workflowTemplateName ??
            t(($) => $.workflowTemplates.defaultBadge),
        })
      : undefined;

  return (
    <>
      {createdFromTemplateLabel && (
        <div className="flex justify-end">
          <p className="text-muted-foreground text-sm">
            {createdFromTemplateLabel}
          </p>
        </div>
      )}
      <div className="grid gap-8 xl:grid-cols-2">
        <div>
          <FormField
            control={form.control}
            name="reviewFrom"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t(($) => $.commons.document.reviewFrom)}</FormLabel>
                <FormDescription>
                  {t(($) => $.commons.document.reviewFromDescription)}
                </FormDescription>
                <DatePicker
                  value={field.value}
                  onChange={(date) => field.onChange(date ?? null)}
                  minDate={
                    reviewFromBounds?.minDueAt
                      ? new Date(reviewFromBounds.minDueAt)
                      : undefined
                  }
                  maxDate={
                    reviewFromBounds?.maxDueAt
                      ? new Date(reviewFromBounds.maxDueAt)
                      : undefined
                  }
                  disabled={isPreviewMode}
                />
                <FormMessage />
                {isBeforeToday(field.value) && (
                  <p className="text-warning text-xs">
                    {t(($) => $.commons.workflow.reviewFromPastWarning)}
                  </p>
                )}
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="reviewTo"
            render={({ field }) => (
              <FormItem className="pt-4">
                <FormLabel>{t(($) => $.commons.document.reviewTo)}</FormLabel>
                <FormDescription>
                  {t(($) => $.commons.document.reviewToDescription)}
                </FormDescription>
                <DatePicker
                  value={field.value}
                  onChange={(date) => field.onChange(date ?? null)}
                  minDate={
                    reviewToBounds?.minDueAt
                      ? new Date(reviewToBounds.minDueAt)
                      : undefined
                  }
                  maxDate={
                    reviewToBounds?.maxDueAt
                      ? new Date(reviewToBounds.maxDueAt)
                      : undefined
                  }
                  disabled={isPreviewMode || workflowHasEnded}
                />
                <FormMessage />
                {isBeforeToday(field.value) && (
                  <p className="text-warning text-xs">
                    {t(($) => $.commons.workflow.reviewToPastWarning)}
                  </p>
                )}
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t(($) => $.commons.workflow.description)}</FormLabel>
              <FormDescription>
                {t(($) => $.commons.workflow.descriptionDescription)}
              </FormDescription>
              <RichTextEditor
                value={(isPreviewMode ? mode.description : field.value) ?? ""}
                onChange={field.onChange}
                className="min-h-40"
                disabled={isPreviewMode}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
}
